import { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { api } from '@/api/growth/dashboard/api';
import {
    GrowthDashboardDaysReq,
    GrowingSizeListItem,
} from '@/api/growth/dashboard/type';
import Tooltip from '@components/common/Tooltip';

interface GrowthGrowingSizeInfoCardProps {
    gfIdx: number;
}

const GrowthGrowingSizeInfoCard = ({
    gfIdx,
}: GrowthGrowingSizeInfoCardProps) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);
    const [loading, setLoading] = useState(true);
    const [growingSizeList, setGrowingSizeList] = useState<
        GrowingSizeListItem[]
    >([]);
    const [selectedDays, setSelectedDays] = useState<number>(14);

    // 생육크기 데이터 조회
    useEffect(() => {
        if (gfIdx) {
            fetchGrowingSizeInfo(gfIdx, selectedDays);
        }
    }, [gfIdx, selectedDays]);

    const fetchGrowingSizeInfo = async (gfIdx: number, days: number) => {
        try {
            setLoading(true);
            const req: GrowthDashboardDaysReq = { gfIdx, days };
            const response = await api.getGrowingSizeCardInfo(req);
            setGrowingSizeList(response.data || []);
        } catch (error) {
            console.error('생육크기 정보 조회 실패:', error);
            setGrowingSizeList([]);
        } finally {
            setLoading(false);
        }
    };

    // 차트 렌더링
    useEffect(() => {
        if (
            !chartRef.current ||
            !growingSizeList ||
            growingSizeList.length === 0
        ) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
            return;
        }

        // 기존 차트 인스턴스 제거
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // 데이터 추출
        const categories = growingSizeList.map(item => {
            const date = new Date(item.measurementDate);
            return `${date.getMonth() + 1}/${date.getDate()}`;
        });

        const heightData = growingSizeList.map(item => item.height || 0);
        const widthData = growingSizeList.map(item => item.width || 0);
        const rootDepthData = growingSizeList.map(item => item.rootDepth || 0);
        const rootDensityData = growingSizeList.map(
            item => item.rootDensity || 0,
        );

        const options = {
            series: [
                {
                    name: '높이',
                    type: 'column',
                    data: heightData,
                },
                {
                    name: '너비',
                    type: 'column',
                    data: widthData,
                },
                {
                    name: '뿌리깊이',
                    type: 'line',
                    data: rootDepthData,
                },
                {
                    name: '뿌리밀도',
                    type: 'line',
                    data: rootDensityData,
                },
            ],
            chart: {
                height: 321,
                type: 'line',
                toolbar: {
                    show: false,
                },
                zoom: {
                    enabled: true,
                },
                dropShadow: {
                    enabled: true,
                    top: 3,
                    left: 0,
                    blur: 4,
                    color: '#000',
                    opacity: 0.1,
                },
            },
            stroke: {
                width: [0, 0, 3, 3],
                curve: 'smooth',
            },
            plotOptions: {
                bar: {
                    columnWidth: '50%',
                    borderRadius: 3,
                },
            },
            fill: {
                opacity: [0.85, 0.85, 1, 1],
                gradient: {
                    inverseColors: false,
                    shade: 'light',
                    type: 'vertical',
                    opacityFrom: 0.85,
                    opacityTo: 0.55,
                    stops: [0, 100, 100, 100],
                },
            },
            labels: categories,
            markers: {
                size: [0, 0, 5, 5],
                colors: ['#FEB019', '#775DD0'],
                strokeColors: '#fff',
                strokeWidth: 2,
                hover: {
                    size: 7,
                },
            },
            xaxis: {
                labels: {
                    rotate: -45,
                    rotateAlways: false,
                },
            },
            yaxis: [
                {
                    title: {
                        text: '수치 (cm)',
                    },
                    labels: {
                        formatter: function (val: number) {
                            return val.toFixed(1);
                        },
                    },
                },
            ],
            colors: ['#00E396', '#008FFB', '#FEB019', '#775DD0'],
            legend: {
                show: true,
                position: 'top',
                markers: {
                    size: 5,
                    shape: 'circle',
                },
            },
            grid: {
                borderColor: '#f1f1f1',
                strokeDashArray: 3,
            },
            tooltip: {
                shared: true,
                intersect: false,
                y: {
                    formatter: function (val: number, opts: any) {
                        const seriesName =
                            opts.w.config.series[opts.seriesIndex].name;
                        if (seriesName === '뿌리밀도') {
                            return val.toFixed(2);
                        }
                        return val.toFixed(1) + ' cm';
                    },
                },
            },
            dataLabels: {
                enabled: false,
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [growingSizeList]);

    // 기간 선택 버튼 핸들러
    const handleDaysChange = (days: number) => {
        setSelectedDays(days);
    };

    return (
        <div className="custom-card card">
            <div className="card-header justify-content-between align-items-center d-sm-flex d-block">
                <div className="card-title mb-sm-0 mb-2">
                    생육크기정보
                    <Tooltip
                        content="작물의 높이, 너비, 뿌리깊이, 뿌리밀도의 변화 추이를 보여줍니다. 지상부와 지하부의 균형있는 성장 패턴을 통해 작물의 건강 상태를 파악할 수 있습니다."
                        customClass="tooltip-dark"
                        placement="top"
                    >
                        <i className="ri-question-line" />
                    </Tooltip>
                </div>
                <div
                    aria-label="Basic example"
                    role="group"
                    className="flex-wrap btn-group"
                >
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 7 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(7)}
                    >
                        7일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 14 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(14)}
                    >
                        14일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 30 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(30)}
                    >
                        30일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 60 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(60)}
                    >
                        60일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === -1 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(-1)}
                    >
                        전체
                    </button>
                </div>
            </div>
            <div className="card-body">
                {loading ? (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: '321px' }}
                    >
                        <div
                            className="spinner-border text-success"
                            role="status"
                        >
                            <span className="visually-hidden">로딩중...</span>
                        </div>
                    </div>
                ) : growingSizeList.length === 0 ? (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: '321px' }}
                    >
                        <div className="text-center text-muted">
                            생육크기 데이터가 없습니다
                        </div>
                    </div>
                ) : (
                    <div ref={chartRef}></div>
                )}
            </div>
        </div>
    );
};

export default GrowthGrowingSizeInfoCard;
